// Multi-video upload, browser side (PLAN-multivideo.md). UI-free: the single-file upload, the duration probe, the
// cross-tab lock, the batch switch and the clock are injected, so the queue is unit-tested without a browser.
// Files go strictly one after another through uploadVideo() (each file already sends 3 parts in parallel; a
// company may hold only 3 open sessions). Server contract unchanged.

import { precheckVideo, videoFileKey, VideoUploadError } from "./videoUpload"

export const VIDEO_BATCH_MAX_FILES = 10
/** Waits for 429 / session_busy per file, before the whole batch pauses. */
export const MAX_RATE_WAITS = 3
/** Waits for a full pipeline (503 busy) per file, before the whole batch pauses. */
export const MAX_BUSY_WAITS = 2
/** Wait phases per run, across all files, before the batch pauses until the user taps "Weiter". */
export const MAX_BATCH_WAITS = 6
const DEFAULT_RATE_WAIT_SEC = 60
const BUSY_WAIT_SEC = 120
const OPEN_WAIT_SEC = 60
const MAX_WAIT_SEC = 180

export type ItemStatus =
  | "waiting" | "checking" | "uploading" | "done" | "already" | "failed" | "skipped" | "cancelled" | "not_started"

export type BatchItem = {
  id: number
  file: File
  status: ItemStatus
  /** German reason for failed / skipped / not started. */
  reason?: string
  sentBytes: number
  /** Failed with a session the server still holds open: may be resumed or discarded. */
  open?: boolean
  /** "Trotzdem hochladen": forget the remembered session once, inside the file lock, before the first attempt. */
  forceNew?: boolean
}

export type BatchState =
  | { kind: "running"; current: number; total: number }
  | { kind: "waiting"; current: number; total: number; seconds: number; why: "rate" | "busy" | "open" }

/** Why a run ended before all files were tried. */
export type BatchStop =
  | { kind: "completed" }
  | { kind: "cancelled" }
  /** The rest of the files keep "not_started"; "Weiter" resumes them. */
  | { kind: "paused"; message: string }
  /** Nothing more can be uploaded right now (daily limit, upload switched off, signed out). */
  | { kind: "stopped"; message: string }

export type UploadOneResult = { alreadyDone: boolean }

export type BatchDeps = {
  /** forceNew: drop the remembered session first (called inside the file lock; only on the first attempt). */
  uploadOne: (file: File, opts: { durationSec?: number; forceNew?: boolean; signal: AbortSignal; onProgress: (sentBytes: number) => void }) => Promise<UploadOneResult>
  /** Duration in seconds, null when unknown. Must settle when the signal aborts. */
  readDuration: (file: File, signal: AbortSignal) => Promise<number | null>
  /** Runs fn under an exclusive cross-tab lock, or returns "held" when another tab holds it. */
  withLock: <T>(name: string, fn: () => Promise<T>) => Promise<T | "held">
  /** Is the batch switch still on? Asked before every file after the first. Throwing counts as "off". */
  batchAllowed: (signal: AbortSignal) => Promise<boolean>
  /** Resolves after ms or early on abort. */
  sleep: (ms: number, signal: AbortSignal) => Promise<void>
  /** 0..1, for jitter. */
  random: () => number
}

let nextId = 1

/**
 * Turns a picked selection into list items: invalid files are skipped right away with a reason, files past the
 * maximum are skipped, and a file picked twice in one selection is skipped (it would share the resume pointer).
 */
export function prepareSelection(files: ArrayLike<File>, max = VIDEO_BATCH_MAX_FILES): BatchItem[] {
  const items: BatchItem[] = []
  const seen = new Set<string>()
  let accepted = 0
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    const item: BatchItem = { id: nextId++, file, status: "waiting", sentBytes: 0 }
    const fingerprint = videoFileKey("", file)
    const pre = precheckVideo(file)
    if (pre) Object.assign(item, { status: "skipped", reason: pre })
    else if (seen.has(fingerprint)) Object.assign(item, { status: "skipped", reason: "Doppelt in der Auswahl" })
    else if (accepted >= max) Object.assign(item, { status: "skipped", reason: `Max. ${max} Videos auf einmal` })
    else { seen.add(fingerprint); accepted++ }
    items.push(item)
  }
  return items
}

/** Files to send again on "Fehlgeschlagene erneut versuchen" / "Weiter": failed ones first (their open sessions). */
export function retryOrder(items: BatchItem[]): number[] {
  const failed = items.filter((i) => i.status === "failed")
  const rest = items.filter((i) => i.status === "cancelled" || i.status === "not_started")
  return [...failed, ...rest].map((i) => i.id)
}

type Verdict =
  | { kind: "wait"; seconds: number; why: "rate" | "busy" | "open" }
  | { kind: "failed"; message: string; open: boolean }
  | { kind: "failed_pause"; message: string; open: boolean }
  | { kind: "stop"; message: string }
  | { kind: "pause"; message: string }
  | { kind: "cancelled" }

const STOP_CODES: Record<string, string> = {
  video_upload_stopped: "Video-Upload ist gerade nicht möglich. Bitte später erneut versuchen.",
  http_404: "Video-Upload ist gerade nicht möglich. Bitte später erneut versuchen.",
  http_410: "Video-Upload ist gerade nicht möglich. Bitte später erneut versuchen.",
  http_401: "Bitte neu anmelden und dann erneut versuchen.",
  http_403: "Kein Zugriff. Bitte Link oder Anmeldung prüfen.",
}
const PAUSE_OPEN = "Bitte zuerst die unterbrochenen Videos fortsetzen oder verwerfen."
const PAUSE_BUSY = "Gerade viel los. Bitte später auf „Weiter“ tippen."
const PAUSE_RATE = "Zu viele Uploads in kurzer Zeit. Bitte gleich auf „Weiter“ tippen."
const PAUSE_NETWORK = "Verbindung unterbrochen. Bitte Verbindung prüfen und auf „Weiter“ tippen, der Upload setzt fort."
const PAUSE_SWITCH = "Mehrfach-Upload ist gerade ausgeschaltet. Bitte Videos einzeln hochladen."

function jitter(seconds: number, random: () => number): number {
  return Math.min(MAX_WAIT_SEC, Math.max(1, Math.round(seconds * (0.8 + 0.4 * random()))))
}

/** Which server answer means what for the batch (PLAN-multivideo.md §9). */
function classify(
  err: unknown, waits: { rate: number; busy: number; open: number; total: number }, holdsOpen: boolean, random: () => number,
): Verdict {
  if (!(err instanceof VideoUploadError)) return { kind: "failed_pause", message: PAUSE_NETWORK, open: true }
  if (err.code === "cancelled") return { kind: "cancelled" }
  const code = err.detail.cause && ["resume_retry", "admit_retry", "complete_retry"].includes(err.code) ? err.detail.cause : err.code
  const budgetLeft = waits.total < MAX_BATCH_WAITS
  if (code === "daily_limit") return { kind: "stop", message: err.message || "Tageslimit erreicht. Bitte morgen weitere Videos hochladen." }
  if (STOP_CODES[code]) return { kind: "stop", message: STOP_CODES[code] }
  if (code === "http_429" || code === "session_busy") {
    if (waits.rate < MAX_RATE_WAITS && budgetLeft) {
      return { kind: "wait", why: "rate", seconds: jitter(err.detail.retryAfterSec ?? DEFAULT_RATE_WAIT_SEC, random) }
    }
    return { kind: "pause", message: PAUSE_RATE }
  }
  if (code === "busy") {
    if (waits.busy < MAX_BUSY_WAITS && budgetLeft) return { kind: "wait", why: "busy", seconds: jitter(BUSY_WAIT_SEC, random) }
    return { kind: "pause", message: PAUSE_BUSY }
  }
  if (code === "too_many_open") {
    // Failed files of this batch keep their sessions open for hours: waiting does not help, resuming does.
    if (!holdsOpen && waits.open < 1 && budgetLeft) return { kind: "wait", why: "open", seconds: jitter(OPEN_WAIT_SEC, random) }
    return { kind: "pause", message: PAUSE_OPEN }
  }
  if (code === "network" || code === "part_failed" || (err.detail.status === 0 && err.detail.cause === "network")) {
    return { kind: "failed_pause", message: PAUSE_NETWORK, open: true }
  }
  // Server refused this file for good (type, length, client id) or another per-file failure: next file.
  const open = !["unsupported_type", "too_long", "invalid_input", "client_id_conflict", "session_closed"].includes(code)
  return { kind: "failed", message: err.message || "Upload fehlgeschlagen.", open }
}

export type RunOptions = {
  /** Lock-name scope: company access (user id, or a hash of the link token). */
  accessKey: string
  /** Ids of the items to send in this run, in order. */
  order: number[]
  signal: AbortSignal
  onItem: (id: number, patch: Partial<BatchItem>) => void
  onState: (s: BatchState) => void
}

/**
 * Sends the given items strictly one after another. Never throws; every item of `order` ends in a final status
 * (done, already, failed, skipped, cancelled or not_started). `items` is read for files and for open sessions.
 */
export async function runVideoBatch(items: BatchItem[], deps: BatchDeps, opts: RunOptions): Promise<BatchStop> {
  const byId = new Map(items.map((i) => [i.id, i]))
  const status = new Map(items.map((i) => [i.id, i.status]))
  const open = new Map(items.map((i) => [i.id, !!i.open]))
  const set = (id: number, patch: Partial<BatchItem>) => {
    if (patch.status) status.set(id, patch.status)
    if (patch.open !== undefined) open.set(id, patch.open)
    opts.onItem(id, patch)
  }
  const order = opts.order.filter((id) => byId.has(id))
  const total = order.length
  const waits = { rate: 0, busy: 0, open: 0, total: 0 }
  const rest = (from: number, s: ItemStatus, reason?: string) => {
    for (const id of order.slice(from)) set(id, { status: s, reason, sentBytes: 0 })
  }

  for (let idx = 0; idx < order.length; idx++) {
    const id = order[idx]
    const file = byId.get(id)!.file
    if (opts.signal.aborted) { rest(idx, "cancelled", "Abgebrochen"); return { kind: "cancelled" } }
    if (idx > 0 && total > 1) {
      let allowed = false
      // Raced against cancel: a stalled request must not keep the queue (and the batch lock) alive.
      const onAbort = new Promise<boolean>((r) => opts.signal.addEventListener("abort", () => r(false), { once: true }))
      try { allowed = await Promise.race([deps.batchAllowed(opts.signal), onAbort]) } catch { allowed = false }
      if (opts.signal.aborted) { rest(idx, "cancelled", "Abgebrochen"); return { kind: "cancelled" } }
      if (!allowed) { rest(idx, "not_started"); return { kind: "stopped", message: PAUSE_SWITCH } }
    }
    opts.onState({ kind: "running", current: idx + 1, total })
    set(id, { status: "checking", reason: undefined, sentBytes: 0 })

    const durationSec = await deps.readDuration(file, opts.signal)
    if (opts.signal.aborted) { rest(idx, "cancelled", "Abgebrochen"); return { kind: "cancelled" } }
    const pre = precheckVideo(file, durationSec)
    if (pre) { set(id, { status: "skipped", reason: pre }); continue }

    waits.rate = 0; waits.busy = 0; waits.open = 0
    let forceNew = !!byId.get(id)!.forceNew
    let finished = false
    while (!finished) {
      const outcome = await deps.withLock(videoFileKey(opts.accessKey, file), async () => {
        try {
          set(id, { status: "uploading" })
          const force = forceNew
          if (force) { forceNew = false; set(id, { forceNew: false }) }
          const r = await deps.uploadOne(file, {
            durationSec: durationSec ?? undefined,
            forceNew: force,
            signal: opts.signal,
            onProgress: (sentBytes) => opts.onItem(id, { sentBytes }),
          })
          return { ok: true as const, r }
        } catch (err) {
          return { ok: false as const, err }
        }
      })
      if (outcome === "held") {
        set(id, { status: "skipped", reason: "Läuft schon in einem anderen Fenster" })
        finished = true
        break
      }
      if (outcome.ok) {
        set(id, outcome.r.alreadyDone
          ? { status: "already", reason: "Bereits hochgeladen", open: false }
          : { status: "done", sentBytes: file.size, open: false })
        finished = true
        break
      }
      const holdsOpen = Array.from(status).some(([other, s]) => other !== id && s === "failed" && open.get(other))
      const v = classify(outcome.err, waits, holdsOpen, deps.random)
      switch (v.kind) {
        case "cancelled":
          rest(idx, "cancelled", "Abgebrochen")
          return { kind: "cancelled" }
        case "stop":
          set(id, { status: "failed", reason: v.message, open: false })
          rest(idx + 1, "not_started")
          return { kind: "stopped", message: v.message }
        case "pause":
          set(id, { status: "not_started", reason: undefined, sentBytes: 0 })
          rest(idx + 1, "not_started")
          return { kind: "paused", message: v.message }
        case "failed_pause":
          set(id, { status: "failed", reason: (outcome.err as Error)?.message || v.message, open: v.open })
          rest(idx + 1, "not_started")
          return { kind: "paused", message: v.message }
        case "failed":
          set(id, { status: "failed", reason: v.message, open: v.open })
          finished = true
          break
        case "wait":
          waits[v.why]++
          waits.total++
          set(id, { status: "waiting" })
          opts.onState({ kind: "waiting", current: idx + 1, total, seconds: v.seconds, why: v.why })
          await deps.sleep(v.seconds * 1000, opts.signal)
          if (opts.signal.aborted) { rest(idx, "cancelled", "Abgebrochen"); return { kind: "cancelled" } }
          opts.onState({ kind: "running", current: idx + 1, total })
          break
      }
    }
  }
  return { kind: "completed" }
}

export function videoCountLabel(n: number): string {
  return `${n} ${n === 1 ? "Video" : "Videos"}`
}

/** "3 Videos angekommen, 1 fehlgeschlagen, 2 übersprungen" (only non-zero tails). */
export function summarizeBatch(items: BatchItem[]): string {
  const count = (...s: ItemStatus[]) => items.filter((i) => s.includes(i.status)).length
  const parts = [`${videoCountLabel(count("done"))} angekommen`]
  const already = count("already")
  const failed = count("failed")
  const skipped = count("skipped")
  const open = count("cancelled", "not_started")
  if (already) parts.push(`${already} bereits hochgeladen`)
  if (failed) parts.push(`${failed} fehlgeschlagen`)
  if (skipped) parts.push(`${skipped} übersprungen`)
  if (open) parts.push(`${open} nicht hochgeladen`)
  return parts.join(", ")
}

// ── browser implementations ─────────────────────────────────────────────────────────────────────

type LockManagerLike = { request: (name: string, opts: { ifAvailable: boolean }, cb: (lock: unknown) => Promise<unknown>) => Promise<unknown> }

function lockManager(): LockManagerLike | null {
  const locks = typeof navigator !== "undefined" ? (navigator as unknown as { locks?: LockManagerLike }).locks : undefined
  return locks && typeof locks.request === "function" ? locks : null
}

/** Batches need cross-tab locks; without them the panel stays single-file (PLAN-multivideo.md §5a). */
export function batchLocksSupported(): boolean {
  return lockManager() !== null
}

/** Exclusive cross-tab lock; without Web Locks the call runs unlocked (single-file upload, as before). */
export async function browserWithLock<T>(name: string, fn: () => Promise<T>): Promise<T | "held"> {
  const locks = lockManager()
  if (!locks) return fn()
  let held = false
  const result = await locks.request(name, { ifAvailable: true }, async (lock) => {
    if (!lock) { held = true; return undefined }
    return fn()
  })
  return held ? "held" : (result as T)
}
